/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import cn from 'classnames';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { PricePlanTier } from '@utils/types/back-office/price-plans-bo.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useCreatePricePlan } from '@utils/api/back-office/price-plans-bo/use-create-price-plan.tsx';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { BaseError } from '@utils/types/shared/base';
import { Plus, Trash2 } from 'lucide-react';

type Props = {
  setName: Dispatch<SetStateAction<string>>;
  setPricePerUnit: Dispatch<SetStateAction<string>>;
  setTiers: React.Dispatch<React.SetStateAction<PricePlanTier[]>>;
  pricePerUnit: string;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
  tiers: PricePlanTier[];
};

export default function CreatePricePlanBoForm({
  setName,
  setPricePerUnit,
  setTiers,
  pricePerUnit,
  isModalOpen,
  setIsModalOpen,
  tiers,
  name
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [updateTiers, setUpdateTiers] = useState<PricePlanTier[]>([
    { start: 0, end: null, price_per_unit: pricePerUnit }
  ]);
  const [nameError, setNameError] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);

  const { mutate: createPricePlan } = useCreatePricePlan();

  const handleTierChange = (
    index: number,
    key: keyof PricePlanTier,
    value: string
  ) => {
    const newTiers = [...updateTiers];
    (newTiers[index][key] as string) = value;
    setUpdateTiers(newTiers);
    setTiers(newTiers);
  };

  const handleAddTier = () => {
    const lastTier = updateTiers[updateTiers.length - 1];
    // @ts-expect-error -- tier.end is typed as string | null at runtime
    const start = lastTier.end !== null ? parseInt(lastTier.end, 10) + 2 : 2;

    const newTiers = [
      ...updateTiers,
      { start, end: null, price_per_unit: pricePerUnit }
    ];

    setUpdateTiers(newTiers);
    setTiers(newTiers);
  };

  const handleRemoveTier = (index: number) => {
    const newTiers = updateTiers.filter((_, i) => i !== index);
    setUpdateTiers(newTiers);
    setTiers(newTiers);
  };

  const submitPricePlan = () => {
    if (!name.trim()) {
      setNameError(t('price-plan-name-required'));
      return;
    } else {
      setNameError('');
    }
    setIsCreating(true);
    const updatedTiers = tiers.map((tier, index) => ({
      ...tier,
      end: index === tiers.length - 1 ? null : tier.end
    }));
    setTiers(updatedTiers);

    createPricePlan(
      { name, pricePerUnit, tiers: updatedTiers },
      {
        onSuccess: () => {
          toast.success(t('price-plan-created-successfully'));
          setIsCreating(false);
          setIsModalOpen(false);
        },
        onError: (error: AxiosError<BaseError>) => {
          const errorMessage =
            error.response?.data?.error_description ||
            error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            t('failed-to-create-the-price-plan');
          toast.error(errorMessage);
          setIsCreating(false);
        }
      }
    );
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainer: {
          borderBottom: `2px solid ${getColour('border')}`
        },
        mainContainerColor: {
          backgroundColor: getColour('secondLayer')
        },
        iconsColor: {
          color: getColour('icon')
        },
        mainText: {
          color: getColour('normalText')
        },
        errorText: {
          color: 'red',
          fontSize: '0.875rem'
        }
      }),
    [getColour]
  );

  const FORM_ID = 'create-price-plan-form';

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      size="medium"
      title={t('create-price-plan')}
      caption={t('priceplans-desc')}
      footerActions={[
        <Button
          onClick={() => setIsModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="smoke"
          key="cancel"
        />,
        <Button
          key="create"
          type="submit"
          form={FORM_ID}
          label={isCreating ? t('creating') : t('create')}
          size="small"
          variant="primary"
        />
      ]}
    >
      <div className="overflow-y-auto max-h-[60vh] sm:max-h-96">
        <div
          className={cn(
            'space-y-4',
            css(styles.mainContainerColor, styles.mainText)
          )}
        >
          <form
            id={FORM_ID}
            className="space-y-4"
            onSubmit={e => {
              e.preventDefault();
              submitPricePlan();
            }}
          >
            <button type="submit" className="hidden" aria-hidden="true" />
            {/* Price Plan Name */}
            <div className="px-0.5">
              <label htmlFor="name" className="block text-sm font-medium mb-2">
                {t('price-plan-name')}
              </label>
              <Input
                id="name"
                name="name"
                placeholder={t('name')}
                onChange={e => setName(e.target.value)}
                type="text"
                autoComplete="given-name"
                className="w-full"
              />
              {nameError && (
                <p className="text-red-500 text-xs mt-1">{nameError}</p>
              )}
            </div>

            {/* Tiers */}
            <div className="space-y-4">
              {updateTiers.map((tier, index) => (
                <div
                  key={index}
                  className="border rounded-2xl p-4 space-y-3"
                  style={{ borderColor: getColour('border') }}
                >
                  {/* Tier Header */}
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold">
                      {t('tier')} {index + 1}
                    </h4>
                    {updateTiers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTier(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        aria-label={t('delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Tier Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label
                        htmlFor={`tier_start_${index}`}
                        className="block text-xs sm:text-sm font-medium mb-1"
                      >
                        {t('start')}
                      </label>
                      <Input
                        id={`tier_start_${index}`}
                        placeholder={t('tier-start')}
                        value={tier.start}
                        onChange={e =>
                          handleTierChange(index, 'start', e.target.value)
                        }
                        type="text"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`tier_end_${index}`}
                        className="block text-xs sm:text-sm font-medium mb-1"
                      >
                        {t('end')}
                      </label>
                      <Input
                        id={`tier_end_${index}`}
                        placeholder={t('tier-end')}
                        value={tier.end || ''}
                        onChange={e =>
                          handleTierChange(index, 'end', e.target.value)
                        }
                        type="text"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`price_unit_${index}`}
                        className="block text-xs sm:text-sm font-medium mb-1"
                      >
                        {t('price-per-unit')}
                      </label>
                      <Input
                        id={`price_unit_${index}`}
                        placeholder={t('price-unit')}
                        value={pricePerUnit}
                        onChange={e => {
                          const newValue = e.target.value;
                          setPricePerUnit(newValue);
                          const newTiers = updateTiers.map(tier => ({
                            ...tier,
                            price_per_unit: newValue
                          }));
                          setUpdateTiers(newTiers);
                          setTiers(newTiers);
                        }}
                        type="text"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Tier Button */}
            <div className="flex justify-end pt-2">
              <Button
                type="button"
                variant="light"
                size="small"
                onClick={handleAddTier}
                icon={<Plus className="h-4 w-4" />}
                label={t('add-tier')}
              />
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
